import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function LeaderSignup() {
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { ...form, role: "leader" });
      login(data.token, data.user);
      toast.success("Leader account activated.");
      navigate("/leader");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B14] flex items-center justify-center p-6">
      <div className="absolute top-6 left-6">
        <Link to="/" data-testid="leader-signup-home-link"><Logo /></Link>
      </div>

      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="hud-line w-10" />
          <span className="hud-label">Lead Pilot · Restricted Access</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl text-white font-light mb-4">
          Command the <span className="text-gold italic">squadron</span>
        </h1>
        <p className="text-slate-400 mb-10 text-sm">
          Leaders see the full team's momentum, warning lights, and can dispatch push messages
          to keep their recruits in formation.
        </p>

        <form onSubmit={submit} className="space-y-5 glass-strong p-8 rounded-sm" data-testid="leader-signup-form">
          <Field label="Full Name" value={form.full_name} onChange={(v) => upd("full_name", v)} testId="leader-name-input" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => upd("email", v)} testId="leader-email-input" />
          <Field label="Password" type="password" value={form.password} onChange={(v) => upd("password", v)} testId="leader-password-input" />

          <button
            type="submit"
            disabled={loading}
            data-testid="leader-signup-submit-button"
            className="btn-gold w-full py-3.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <ShieldCheck size={16} />
            {loading ? "Provisioning..." : "Activate Leader Access"}
          </button>

          <p className="text-xs text-slate-500 text-center pt-2">
            Already have access?{" "}
            <Link to="/login" className="text-gold hover:underline" data-testid="leader-signup-to-login">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, type = "text", value, onChange, testId }) => (
  <label className="block">
    <div className="hud-label text-[10px] mb-2">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      required
      className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-gold transition-colors"
    />
  </label>
);
